// frontend/src/components/mcbim/RoomColoringBar.tsx
import React, { useMemo } from "react";
import { Badge, Button, Group, Menu, ScrollArea, Text, Tooltip } from "@mantine/core";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectRoomColorings } from "../Room/roomSelectors";
import {
  ROOM_COLORING_IDS,
  ERoomColoringCategoryId,
  IRoomColoring,
} from "../RoomColoring";
import { ROOM_COLORING_CATEGORIES } from "../RoomColoring/roomColoringConstants";
import { ModuleToolBarShell } from "../DesignTab/ModuleBars/ModuleToolBarShell";
import { setActiveRoomColoringIdThunk } from "../DesignTab/designThunk";
import { MediaAssetViewer } from "../MediaAssets/MediaAssetViewer";
import { MEDIA_ASSET_KEYS } from "@multiconsult-group/multiplacer-core";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const makePlaceholder = (w = 14) => <span style={{ width: w, display: "inline-block" }} />;

const groupByCategory = (items: IRoomColoring[]): Record<string, IRoomColoring[]> => {
  return items.reduce(
    (acc, item) => {
      const k = item.categoryId ?? ERoomColoringCategoryId.Annet;
      (acc[k] ??= []).push(item);
      return acc;
    },
    {} as Record<string, IRoomColoring[]>
  );
};

const sortRoomColorings = (a: IRoomColoring, b: IRoomColoring) => {
  const ao = a.order ?? 9999;
  const bo = b.order ?? 9999;
  if (ao !== bo) return ao - bo;
  return a.title.localeCompare(b.title);
};

const getCategoryTitle = (categoryId: string): string => {
  const hit = ROOM_COLORING_CATEGORIES.find((c) => c.id === categoryId);
  return hit?.titleNo ?? categoryId;
};

const getOrderedCategoryIds = (grouped: Record<string, IRoomColoring[]>) => {
  const known = [...ROOM_COLORING_CATEGORIES].sort((a, b) => a.order - b.order).map((c) => c.id);

  const unknown = Object.keys(grouped).filter(
    (id) => !known.includes(id as ERoomColoringCategoryId)
  );

  return [...known, ...unknown];
};

/* -------------------------------------------------------------------------- */
/*  Category dropdown (egen komp)                                              */
/* -------------------------------------------------------------------------- */

interface ICategoryDropdownProps {
  categoryId: string;
  items: IRoomColoring[];
  activeId?: string;
  onSelect: (id: string | undefined) => void;
}

const RoomColoringCategoryDropdown: React.FC<ICategoryDropdownProps> = ({
  categoryId,
  items,
  activeId,
  onSelect,
}) => {
  const categoryTitle = getCategoryTitle(categoryId);

  const activeInCategory = items.find((f) => f.id === activeId);

  return (
    <Menu withinPortal position="bottom-start" shadow="md" width={420}>
      <Menu.Target>
        <Tooltip
          withArrow
          label={
              activeInCategory?.title
                ? `${categoryTitle}: ${activeInCategory.title}`
              : `Velg romfargelegging i ${categoryTitle}`
          }
        >
          <Button
            size="xs"
            radius="xl"
            variant={activeInCategory ? "filled" : "outline"}
            color={activeInCategory ? "blue" : undefined}
            rightSection={<FiChevronDown size={14} />}
          >
            <Group gap={6} wrap="nowrap">
              <Text size="xs" fw={600}>
                {categoryTitle}
              </Text>
              {activeInCategory ? (
                <Badge variant="light" size="xs">
                  {activeInCategory.title}
                </Badge>
              ) : null}
            </Group>
          </Button>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        {/* Nullstill KUN denne kategorien (dvs: hvis aktivt filter ligger her) */}
        <Menu.Item
          onClick={() => onSelect(undefined)}
          leftSection={!activeInCategory ? <FiCheck size={14} /> : makePlaceholder()}
          disabled={!activeInCategory}
        >
          <Text size="sm">Ingen i denne kategorien</Text>
        </Menu.Item>

        <Menu.Divider />

        <ScrollArea type="auto" scrollbarSize={6} style={{ maxHeight: 260 }}>
          {items.sort(sortRoomColorings).map((filter) => {
            const isSelected = filter.id === activeId;
            const tooltip =
              filter.explanation?.longNo ?? filter.explanation?.shortNo ?? filter.title;

            return (
              <Tooltip key={filter.id} label={tooltip} withArrow position="left" multiline w={340}>
                <Menu.Item
                  onClick={() => onSelect(filter.id)}
                  leftSection={isSelected ? <FiCheck size={14} /> : makePlaceholder()}
                  rightSection={isSelected ? <Badge size="xs">Aktiv</Badge> : null}
                >
                  <Text size="sm" truncate>
                    {filter.title}
                  </Text>
                </Menu.Item>
              </Tooltip>
            );
          })}
        </ScrollArea>
      </Menu.Dropdown>
    </Menu>
  );
};

/* -------------------------------------------------------------------------- */
/*  Bar                                                                         */
/* -------------------------------------------------------------------------- */

export const RoomColoringBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const roomColorings = useAppSelector(selectRoomColorings);
  const activeRoomColoringId = useAppSelector((state) => state.design.activeRoomColoringId);

  const setActive = (id: string | undefined) => {
    dispatch(setActiveRoomColoringIdThunk(id as unknown as ROOM_COLORING_IDS));
  };

  const grouped = useMemo(() => groupByCategory(roomColorings), [roomColorings]);

  const orderedCategoryIds = useMemo(() => getOrderedCategoryIds(grouped), [grouped]);

  return (
    <ModuleToolBarShell>
      <MediaAssetViewer assetKey={MEDIA_ASSET_KEYS.COLORFILTER} mode="ICON" />
      <ScrollArea type="auto" scrollbarSize={6} offsetScrollbars>
        <Group gap={6} wrap="nowrap" p={4}>
          {/* "Ingen" / reset */}
          <Tooltip label="Ingen romfargelegging" withArrow>
            <Button
              size="xs"
              radius="xl"
              variant={!activeRoomColoringId ? "filled" : "outline"}
              color={!activeRoomColoringId ? "blue" : undefined}
              onClick={() => setActive(undefined)}
            >
              Ingen
            </Button>
          </Tooltip>

          {/* En dropdown per hovedkategori */}
          {orderedCategoryIds.map((categoryId) => {
            const items = grouped[categoryId] ?? [];
            if (items.length === 0) return null;

            return (
              <RoomColoringCategoryDropdown
                key={categoryId}
                categoryId={categoryId}
                items={items}
                activeId={activeRoomColoringId}
                onSelect={(id) => {
                  // "Ingen i denne kategorien" skal bare slå av hvis aktivt filter faktisk ligger i kategorien
                  if (id === undefined) {
                    const activeInThisGroup = items.some((f) => f.id === activeRoomColoringId);
                    if (activeInThisGroup) setActive(undefined);
                    return;
                  }
                  setActive(id);
                }}
              />
            );
          })}
        </Group>
      </ScrollArea>
    </ModuleToolBarShell>
  );
};
